
import { supabase } from '@/integrations/supabase/client';
import { LogEntry, LogQueryOptions, LogQueryResult } from './types';

/**
 * Service for storing and retrieving logs from Supabase
 */
export class LogStorageService {
  /**
   * Store log in Supabase
   */
  public async storeLog(logEntry: LogEntry): Promise<void> {
    try {
      const { error } = await supabase
        .from('app_logs')
        .insert([{
          level: logEntry.level,
          message: logEntry.message,
          details: logEntry.details || null,
          source: logEntry.source,
          user_id: logEntry.user_id
        }]);

      if (error) {
        console.error('Failed to store log entry:', error);
      }
    } catch (e) {
      console.error('Error storing log entry:', e);
    }
  }

  /**
   * Fetch logs with pagination and filtering
   */
  public async getLogs({
    page = 1,
    limit = 50,
    level,
    source,
    search,
    startDate,
    endDate
  }: LogQueryOptions = {}): Promise<LogQueryResult> {
    try {
      let query = supabase
        .from('app_logs')
        .select('*', { count: 'exact' });

      // Apply filters
      if (level) {
        if (Array.isArray(level)) {
          query = query.in('level', level);
        } else {
          query = query.eq('level', level);
        }
      }

      if (source) {
        query = query.eq('source', source);
      }

      if (search) {
        query = query.ilike('message', `%${search}%`);
      }

      if (startDate) {
        query = query.gte('timestamp', startDate.toISOString());
      }

      if (endDate) {
        query = query.lte('timestamp', endDate.toISOString());
      }

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      
      query = query
        .order('timestamp', { ascending: false })
        .range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching logs:', error);
        return { logs: [], count: 0 };
      }

      return { 
        logs: data as LogEntry[], 
        count: count || 0 
      };
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      return { logs: [], count: 0 };
    }
  }
}
